import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { BusinessUnitService, CreateBusinessUnitDto } from '../../core/services/business-unit.service';
import { AuthenticationService } from '../../core/services/authentication.service';
import { SubscriptionService, SubscriptionPlan } from '../../core/services/subscription.service';
import { SubscriptionPlansStore } from '../../core/stores/subscription-plans.store';

type Step = 'details' | 'plan';
type BillingCycle = 'Monthly' | 'Annual';

@Component({
  selector: 'app-business-unit-setup',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './business-unit-setup.component.html'
})
export class BusinessUnitSetupComponent implements OnInit {
  private fb = inject(FormBuilder);
  private buService = inject(BusinessUnitService);
  private authService = inject(AuthenticationService);
  private subs = inject(SubscriptionService);
  private plansStore = inject(SubscriptionPlansStore);

  step = signal<Step>('details');
  readonly plans = computed(() => [...this.plansStore.plans()].sort((a, b) => a.displayOrder - b.displayOrder));
  selectedPlanId = signal<string | null>(null);
  selectedCycle = signal<BillingCycle>('Monthly');
  startTrial = signal(true);
  isLoadingPlans = signal(false);

  form: FormGroup = this.fb.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
    code: [''],
    description: [''],
    contactEmail: ['', [Validators.email]],
    contactPhone: [''],
    chairman: [''],
    secretary: ['']
  });

  isSubmitting = false;
  errorMessage = '';

  get f() { return this.form.controls; }

  ngOnInit(): void {
    this.loadPlans();
  }

  private async loadPlans(): Promise<void> {
    this.isLoadingPlans.set(true);
    try {
      await this.plansStore.loadPublic();
      const ordered = this.plans();
      // Default selection: the first plan in display order, or STARTER if present.
      const starter = ordered.find(p => p.code?.toUpperCase() === 'STARTER');
      this.selectedPlanId.set((starter ?? ordered[0])?.id ?? null);
    } finally {
      this.isLoadingPlans.set(false);
    }
  }

  goToPlanStep(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    this.errorMessage = '';
    this.step.set('plan');
  }

  backToDetails(): void {
    this.step.set('details');
  }

  selectPlan(planId: string): void {
    this.selectedPlanId.set(planId);
  }

  setCycle(c: BillingCycle): void {
    this.selectedCycle.set(c);
  }

  toggleTrial(): void {
    this.startTrial.update(v => !v);
  }

  priceFor(plan: SubscriptionPlan): number {
    return this.selectedCycle() === 'Annual' ? plan.priceAnnual : plan.priceMonthly;
  }

  isFreePlan(plan: SubscriptionPlan): boolean {
    return plan.priceMonthly === 0 && plan.priceAnnual === 0;
  }

  onSubmit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.step.set('details');
      return;
    }
    if (!this.selectedPlanId()) {
      this.errorMessage = 'Pick a plan to continue.';
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';

    const v = this.form.value;
    const payload: CreateBusinessUnitDto = {
      name: v.name?.trim(),
      code: v.code?.trim() || undefined,
      description: v.description?.trim() || undefined,
      contactEmail: v.contactEmail?.trim() || undefined,
      contactPhone: v.contactPhone?.trim() || undefined,
      chairman: v.chairman?.trim() || undefined,
      secretary: v.secretary?.trim() || undefined,
      emailEnabled: false,
      smsEnabled: false
    };

    this.buService.create(payload).subscribe({
      next: (res) => {
        if (res?.success) {
          this.buService.invalidateExistsCache();
          const buId = res.data?.id;
          if (typeof buId === 'string' && buId) {
            this.startSubscriptionThenSwitch(buId);
          } else {
            window.location.href = '/home';
          }
        } else {
          this.errorMessage = res?.message || 'Failed to create committee.';
          this.isSubmitting = false;
        }
      },
      error: () => {
        this.errorMessage = 'An error occurred. Please try again.';
        this.isSubmitting = false;
      }
    });
  }

  private startSubscriptionThenSwitch(buId: string): void {
    const planId = this.selectedPlanId();
    if (!planId) {
      this.authService.switchBusinessUnit(buId).subscribe({
        next: () => { window.location.href = '/home'; },
        error: () => { window.location.href = '/home'; }
      });
      return;
    }

    const plan = this.plans().find(p => p.id === planId);
    const wantsTrial = this.startTrial() && (plan?.trialDays ?? 0) > 0 && !this.isFreePlan(plan!);

    this.subs.start(buId, {
      planId,
      billingCycle: this.selectedCycle(),
      startTrial: wantsTrial
    }).subscribe({
      // Subscription start is best-effort here — even if it fails, the BU exists and the
      // owner can pick a plan from /billing afterwards.
      next: () => this.switchAndGoHome(buId),
      error: () => this.switchAndGoHome(buId)
    });
  }

  private switchAndGoHome(buId: string): void {
    this.authService.switchBusinessUnit(buId).subscribe({
      next: () => { window.location.href = '/home'; },
      error: () => { window.location.href = '/home'; }
    });
  }
}
